import { FORM_FIELDS, formsOf, holdsForm, SITE_FIELDS, SOIL_FORMS } from './installationFormFields';

export interface FormOrigin {
    generation: 'older' | 'current' | 'both' | null;
    path: 'pose' | 'landOwner' | 'commissioning' | null;
    /** False only when this installation's process is known and does not ask for that field. */
    isAsked: (field: string) => boolean;
}

const ALL_FIELDS = [...FORM_FIELDS, ...SITE_FIELDS];

// Only the pose step holds these: the land owner step stops at _form2, _form3, _form5 and _form16, the commissioning
// step at _form2, _form3 and _form16.
const POSE_ONLY_FORMS = [
    '_form4',
    '_form6',
    '_form7',
    ...SOIL_FORMS,
    '_form11',
    '_form12',
    '_form13',
    '_form14',
    '_form15',
];

const holdsKey = (notes: any[], form: string, key: string) =>
    notes.some((note) => {
        const held = formsOf(note)[form];
        return !!held && typeof held === 'object' && held[key] !== undefined;
    });

const generationOf = (notes: any[]): FormOrigin['generation'] => {
    const usesKeysOf = (process: 'older' | 'current') =>
        ALL_FIELDS.some(({ sources }) =>
            sources.some((source) => source.process === process && holdsKey(notes, source.form, source.key)),
        );
    const isOlder = usesKeysOf('older');
    const isCurrent = usesKeysOf('current');
    if (isOlder && isCurrent) {
        return 'both';
    }
    if (isOlder) {
        return 'older';
    }
    return isCurrent ? 'current' : null;
};

const pathOf = (notes: any[]): FormOrigin['path'] => {
    if (POSE_ONLY_FORMS.some((form) => holdsForm(notes, form))) {
        return 'pose';
    }
    if (holdsForm(notes, '_form5')) {
        return 'landOwner';
    }
    return ['_form2', '_form3', '_form16'].some((form) => holdsForm(notes, form)) ? 'commissioning' : null;
};

/**
 * Which process wrote the forms of an installation, from its history. See docs/rikutec-form-fields.md.
 *
 * The notes do not say which process or node wrote them, and node ids differ between processes, so this reads what the
 * forms hold. Without any form there is nothing to tell a question nobody asked from a question nobody answered, so
 * every field then counts as asked.
 */
const identifyFormOrigin = (notes: any[]): FormOrigin => {
    const path = pathOf(notes);

    const isAsked = (field: string) => {
        if (!path) {
            return true;
        }
        if (field === 'soilTypology') {
            return SOIL_FORMS.some((form) => holdsForm(notes, form));
        }
        const known = ALL_FIELDS.find((candidate) => candidate.field === field);
        if (!known) {
            return true;
        }
        // A radio is always saved once its form is filled in, so a missing key is a question this form did not ask.
        // An optional text field may simply not have been saved, so its form is all that can be told.
        return known.type === 'answer'
            ? known.sources.some(({ form, key }) => holdsKey(notes, form, key))
            : known.sources.some(({ form }) => holdsForm(notes, form));
    };

    return { generation: path ? generationOf(notes) : null, path, isAsked };
};

export default identifyFormOrigin;
